/**
 * TenantOpsActions — governor operational actions on a specific tenant.
 *
 * Three actions:
 *   1. Retry onboarding  → re-enqueues a fresh onboarding_jobs doc
 *   2. Grant credits     → add USD to tenant_billing wallet + audit log
 *   3. Impersonate       → mint a 15-min CRM token to log in AS the tenant admin
 *
 * Each action shows a dialog for confirmation + reason (audit-logged).
 */
import { useState } from 'react';
import { governanceAPI } from '../../../lib/governanceApi';
import { toast } from 'sonner';
import { RefreshCw, Gift, UserCheck, ExternalLink } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Label } from '../../../components/ui/label';

export default function TenantOpsActions({ tenantId, tenantName, onChange }) {
  const [openDialog, setOpenDialog] = useState(null); // 'retry' | 'grant' | 'impersonate' | null
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState('');
  const [amount, setAmount] = useState('5');
  const [duration, setDuration] = useState(15);

  const close = () => {
    setOpenDialog(null);
    setReason('');
    setAmount('5');
    setDuration(15);
    setBusy(false);
  };

  // ── Retry onboarding ──
  const handleRetry = async () => {
    if (reason.trim().length < 3) {
      toast.error('Reason is required (min 3 chars).');
      return;
    }
    setBusy(true);
    try {
      const res = await governanceAPI.retryTenantOnboarding(tenantId, reason.trim());
      toast.success(`Onboarding re-queued · job ${res.data.job_id.slice(0, 8)}…`);
      if (onChange) onChange();
      close();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Retry failed');
      setBusy(false);
    }
  };

  // ── Grant credits ──
  const handleGrant = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0 || amt > 1000) {
      toast.error('Amount must be > 0 and ≤ $1000.');
      return;
    }
    if (reason.trim().length < 3) {
      toast.error('Reason is required (min 3 chars).');
      return;
    }
    setBusy(true);
    try {
      const res = await governanceAPI.grantTenantCredits(tenantId, amt, reason.trim());
      toast.success(`Granted $${amt.toFixed(2)} · new balance $${res.data.balance_after_usd.toFixed(2)}`);
      if (onChange) onChange();
      close();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Grant failed');
      setBusy(false);
    }
  };

  // ── Impersonate ──
  const handleImpersonate = async () => {
    if (reason.trim().length < 3) {
      toast.error('Reason is required (min 3 chars).');
      return;
    }
    setBusy(true);
    try {
      const res = await governanceAPI.impersonateTenant(tenantId, reason.trim(), duration);
      const { access_token, user, expires_at } = res.data;

      // Stash current CRM session if any, then replace with impersonation token
      const prev = localStorage.getItem('access_token');
      if (prev) localStorage.setItem('_governor_prev_token', prev);

      localStorage.setItem('access_token', access_token);
      localStorage.setItem('user', JSON.stringify(user));
      localStorage.setItem('_impersonation_active', JSON.stringify({
        governor_session: true,
        tenant_id: tenantId,
        tenant_name: tenantName,
        user_email: user.email,
        expires_at,
      }));

      toast.success(`Impersonating ${user.email} · expires ${new Date(expires_at).toLocaleTimeString()}`);

      // Open CRM in a new tab so governor session stays untouched
      window.open('/dashboard', '_blank', 'noopener,noreferrer');
      close();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Impersonation failed');
      setBusy(false);
    }
  };

  return (
    <>
      <Card data-testid="tenant-ops-actions">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Governor Actions</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={() => setOpenDialog('retry')}
            data-testid="tenant-action-retry"
          >
            <RefreshCw className="w-4 h-4 mr-2" /> Retry Onboarding
          </Button>
          <Button
            variant="outline"
            onClick={() => setOpenDialog('grant')}
            data-testid="tenant-action-grant"
          >
            <Gift className="w-4 h-4 mr-2" /> Grant Credits
          </Button>
          <Button
            variant="outline"
            onClick={() => setOpenDialog('impersonate')}
            className="border-amber-500 text-amber-700 hover:bg-amber-50 dark:text-amber-400 dark:hover:bg-amber-950"
            data-testid="tenant-action-impersonate"
          >
            <UserCheck className="w-4 h-4 mr-2" /> Impersonate
          </Button>
        </CardContent>
      </Card>

      {/* Retry Onboarding Dialog */}
      <Dialog open={openDialog === 'retry'} onOpenChange={(o) => !o && close()}>
        <DialogContent data-testid="retry-dialog">
          <DialogHeader>
            <DialogTitle>Retry Onboarding</DialogTitle>
            <DialogDescription>
              Re-enqueues a fresh onboarding job for <strong>{tenantName}</strong>. The tenant's stored inputs (website, team size) will be reused. Existing leads will remain; new leads will be appended.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Label htmlFor="retry-reason">Reason (audit-logged)</Label>
            <Textarea
              id="retry-reason"
              placeholder="e.g. Apollo outage was blocking initial signup"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid="retry-reason-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={close} disabled={busy}>Cancel</Button>
            <Button onClick={handleRetry} disabled={busy} data-testid="retry-submit">
              {busy ? 'Queueing…' : 'Retry Now'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Grant Credits Dialog */}
      <Dialog open={openDialog === 'grant'} onOpenChange={(o) => !o && close()}>
        <DialogContent data-testid="grant-dialog">
          <DialogHeader>
            <DialogTitle>Grant Credits</DialogTitle>
            <DialogDescription>
              Add USD credits to <strong>{tenantName}</strong>'s AI wallet. Max $1000 per action. All grants are audit-logged.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Label htmlFor="grant-amount">Amount (USD)</Label>
            <Input
              id="grant-amount"
              type="number"
              step="0.01"
              min="0.01"
              max="1000"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              data-testid="grant-amount-input"
            />
            <Label htmlFor="grant-reason">Reason (audit-logged)</Label>
            <Textarea
              id="grant-reason"
              placeholder="e.g. Goodwill credit after Apollo outage"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid="grant-reason-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={close} disabled={busy}>Cancel</Button>
            <Button onClick={handleGrant} disabled={busy} data-testid="grant-submit">
              {busy ? 'Granting…' : `Grant $${amount}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Impersonate Dialog */}
      <Dialog open={openDialog === 'impersonate'} onOpenChange={(o) => !o && close()}>
        <DialogContent data-testid="impersonate-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-amber-600" />
              Impersonate Tenant
            </DialogTitle>
            <DialogDescription>
              Mints a short-lived CRM token and opens <strong>{tenantName}</strong>'s CRM in a new tab.
              Every impersonation is audit-logged. Max 60 min.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Label htmlFor="imp-duration">Duration (minutes)</Label>
            <Input
              id="imp-duration"
              type="number"
              min="5"
              max="60"
              value={duration}
              onChange={(e) => setDuration(Math.max(5, Math.min(60, parseInt(e.target.value) || 15)))}
              data-testid="impersonate-duration-input"
            />
            <Label htmlFor="imp-reason">Reason (audit-logged)</Label>
            <Textarea
              id="imp-reason"
              placeholder="e.g. Debugging reported Kanban loading issue"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid="impersonate-reason-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={close} disabled={busy}>Cancel</Button>
            <Button
              onClick={handleImpersonate}
              disabled={busy}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="impersonate-submit"
            >
              {busy ? 'Issuing token…' : (
                <><ExternalLink className="w-4 h-4 mr-2" /> Impersonate &amp; Open CRM</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
